import { Button, ChirpConfig, ChirpConfigs, ChirpOperationMode, CustomClaimValue, getOperationModeDisplayName, getTargetTypesDisplayName, MaxServerIntValue, OperationMode, TrackerConfigs, TrackerConfigType, OptionValue } from "@app/shared";
import { FC, useState, useEffect } from "react";
import { useConfirmDialog, useFetch, useModel } from "../../../hooks";
import { CheckboxInput, IconLink, Icons, Input, InputRow, MultiSelect, Select, SortableTable, TextArea } from "../../shared";
import { AuthorizeView } from "../../shared/AuthorizeView";
import { TargetTypeFilterInput } from "../facility/TargetTypeFilterInput";

const getDistanceDescription = (config: ChirpConfig | undefined) => {
    if (!config) return "";
    const { minDistanceM, maxDistanceM } = config;
    if (!minDistanceM && !maxDistanceM) {
        return "Any";
    }
    if (minDistanceM === maxDistanceM) {
        return `~${minDistanceM} m`;
    }
    return `${minDistanceM ? minDistanceM : "0"} - ${maxDistanceM ? maxDistanceM : "Any"} m`;
};
const getAngleDescription = (config: ChirpConfig | undefined) => {
    if (!config) return "";

    if (config.angleToTrafficDeg === undefined || config.angleToTrafficDeg === null) {
        return "Any";
    }
    return `${config.angleToTrafficDeg}°`;
};
const getSpeedDescription = (config: ChirpConfig | undefined) => {
    if (!config) return "";
    const { minBoresightSpeedM, maxBoresightSpeedM } = config;
    if (!minBoresightSpeedM && !maxBoresightSpeedM) {
        return "Any";
    }
    if (minBoresightSpeedM === maxBoresightSpeedM) {
        return `~${minBoresightSpeedM} m/s`;
    }
    return `${minBoresightSpeedM ? minBoresightSpeedM : "Any"} - ${maxBoresightSpeedM ? maxBoresightSpeedM : "Any"} m/s`;
};
const getTargetTypeDescription = (config: ChirpConfig | undefined) => {
    if (!config) return "";
    const { targetType } = config;
    if (targetType === undefined || targetType === MaxServerIntValue || targetType === null || targetType === 0) {
        return "Any";
    }
    return getTargetTypesDisplayName(targetType);
};

const chirpFlagForMode = (mode: OperationMode): ChirpOperationMode => {
    switch (mode) {
        case OperationMode.Traffic_Counting: return ChirpOperationMode.Traffic_Counting;
        case OperationMode.Wrong_Way_Detection: return ChirpOperationMode.Wrong_Way_Detection;
        case OperationMode.Parking_Monitor: return ChirpOperationMode.Parking_Monitor;
        case OperationMode.Intersection_Management: return ChirpOperationMode.Intersection_Management;
        case OperationMode.Train_Station: return ChirpOperationMode.Train_Station;
        case OperationMode.Attenuator_Truck: return ChirpOperationMode.Attenuator_Truck;
        default: return ChirpOperationMode.None;
    }
};

const allOperationModes: OperationMode[] = [
    OperationMode.Traffic_Counting,
    OperationMode.Wrong_Way_Detection,
    OperationMode.Parking_Monitor,
    OperationMode.Intersection_Management,
    OperationMode.Train_Station,
    OperationMode.Attenuator_Truck,
];

const chirpModeOptions = allOperationModes.map((m) => ({
    value: chirpFlagForMode(m),
    label: getOperationModeDisplayName(m),
}));

const getSelectedModeFlags = (config: ChirpConfig | undefined): ChirpOperationMode[] => {
    if (!config) return [];
    if (config.deviceModes !== undefined && config.deviceModes !== null) {
        return chirpModeOptions.map((o) => o.value).filter((v) => (config.deviceModes! & v) !== 0);
    }
    if (config.deviceMode !== undefined && config.deviceMode !== null) {
        return [chirpFlagForMode(config.deviceMode)];
    }
    return [];
};

const getModeDescription = (config: ChirpConfig | undefined) => {
    const flags = getSelectedModeFlags(config);
    if (!flags.length) return "Any";
    return chirpModeOptions.filter((o) => flags.includes(o.value)).map((o) => o.label).join(", ");
};

export const ChirpLibrary: FC = (props) => {
    const [configs, , configApi] = useFetch(ChirpConfigs.getAll);
    const [trackerConfigs, , trackerConfigApi] = useFetch(TrackerConfigs.getAll);
    const confirmDialog = useConfirmDialog();
    const [curConfig, configHelpers] = useModel(ChirpConfig);
    const [isEditing, setIsEditing] = useState(false);
    const [radarTrackerConfigOptions, setRadarTrackerConfigOptions] = useState<OptionValue[]>([]);
    const [videoTrackerConfigOptions, setVideoTrackerConfigOptions] = useState<OptionValue[]>([]);

    const handleDeleteOnClick = (config: ChirpConfig) => {
        confirmDialog.show("Delete Config?", `Are you sure you want to delete the config named [${config.name}]?`, "Delete", async () => {
            await ChirpConfigs.deleteConfig(config.id);
            await configApi.refreshData();
        });
    };
    const handleOnRowClick = (config: ChirpConfig) => {
        if (isEditing) {
            configHelpers.replaceModel(config);
            return;
        }
        configHelpers.replaceModel(config);
        setIsEditing(true);
    };

    const handleCancelEditOnClick = () => {
        setIsEditing(false);
        configApi.refreshData();
    };

    const handleAddNewOnClick = () => {
        configHelpers.replaceModel(new ChirpConfig());
        setIsEditing(true);
    };

    const handleSaveOnClick = async () => {
        await ChirpConfigs.save(curConfig as ChirpConfig);
        setIsEditing(false);
        await configApi.refreshData();
    };

    const handleModesChange = (selected: Array<string | number>) => {
        const mask = selected.reduce<number>((acc, v) => acc | Number(v), 0);
        // For multi-mode configs, `deviceMode` is set to the first selected mode by
        // OperationMode enum order. Old firmware that only reads `deviceMode` will
        // therefore treat the config as single-mode and this is intentional: old firmware can't
        // represent multi-mode, so the config is still usable under at least one mode
        // rather than invisible entirely. Later we can remove this behavior when we know all devices
        // got updated to the latest version
        const primary = mask === 0
            ? undefined
            : allOperationModes.find((m) => (mask & chirpFlagForMode(m)) !== 0);
        configHelpers.replaceModel({
            ...(curConfig as ChirpConfig),
            deviceModes: mask === 0 ? undefined : (mask as ChirpOperationMode),
            deviceMode: primary,
        } as ChirpConfig);
    };

    useEffect(() => {
        if (trackerConfigApi.isLoading || !trackerConfigs) return;
        
        const radarOptions: OptionValue[] = trackerConfigs
            .filter(config => config.type === TrackerConfigType.Radar)
            .map((config) => ({
                label: config.name || "Unnamed Config",
                value: config.id || "",
            }));
            
        const videoOptions: OptionValue[] = trackerConfigs
            .filter(config => config.type === TrackerConfigType.Video)
            .map((config) => ({
                label: config.name || "Unnamed Config",
                value: config.id || "",
            }));
            
        setRadarTrackerConfigOptions(radarOptions);
        setVideoTrackerConfigOptions(videoOptions);
    }, [trackerConfigs, trackerConfigApi.isLoading]);

    const editView = (
        <>
            <InputRow>
                <Input label="Name" {...configHelpers.bindingsFor("name")} />
            </InputRow>
            <InputRow>
                <Input label="Description" {...configHelpers.bindingsFor("description")} />
            </InputRow>
            <InputRow>
                <MultiSelect
                    label="Device Modes"
                    options={chirpModeOptions}
                    value={getSelectedModeFlags(curConfig as ChirpConfig)}
                    onChange={handleModesChange}
                    allowEmpty={true}
                />
            </InputRow>
            <InputRow>
                <CheckboxInput label="Bi-Directional Traffic" {...configHelpers.bindingsFor("biDirectional")} checked={!!curConfig.biDirectional} />
            </InputRow>
            <InputRow>
                <Input label="Angle (°)" {...configHelpers.bindingsFor("angleToTrafficDeg")} />
            </InputRow>
            <InputRow>
                <Input label="Min Distance (m)" {...configHelpers.bindingsFor("minDistanceM")} />
            </InputRow>
            <InputRow>
                <Input label="Max Distance (m)" {...configHelpers.bindingsFor("maxDistanceM")} />
            </InputRow>
            <InputRow>
                <Input label="Min Speed (m/s)" {...configHelpers.bindingsFor("minBoresightSpeedM")} />
            </InputRow>
            <InputRow>
                <Input label="Max Speed (m/s)" {...configHelpers.bindingsFor("maxBoresightSpeedM")} />
            </InputRow>
            <InputRow>
                <TargetTypeFilterInput label="Target Types" {...configHelpers.bindingsFor("targetType")} />
            </InputRow>
            <InputRow>
                <Select label="Radar Tracker Config" options={radarTrackerConfigOptions} isLoadingOptions={trackerConfigApi.isLoading} emptyOptionText="Select Radar Tracker Config" emptyOptionDisabled={false} {...configHelpers.bindingsFor("radarTrackerConfigId")} />
            </InputRow>
            <InputRow>
                <Select label="Video Tracker Config" options={videoTrackerConfigOptions} isLoadingOptions={trackerConfigApi.isLoading} emptyOptionText="Select Video Tracker Config" emptyOptionDisabled={false} {...configHelpers.bindingsFor("videoTrackerConfigId")} />
            </InputRow>
            <InputRow>
                <TextArea label="CHIRP Config" rows={20} {...configHelpers.bindingsFor("chirpConfig")} />
            </InputRow>
            <div className="absolute bottom-0 right-10 p-5 flex flex-row gap-2">
                <Button onClick={handleSaveOnClick}>Save</Button>
                <Button onClick={handleCancelEditOnClick}>Cancel</Button>
            </div>
        </>
    );

    const tableView = (
        <>
            <SortableTable
                onRowClick={handleOnRowClick}
                data={configs}
                emptyValue="No Chirp Configs Found"
                isLoading={configApi.isLoading}
                columns={[
                    { header: "Name", dataKey: "name", width: 150 },
                    { header: "Mode", dataKey: "deviceMode", width: 220, renderFunc: getModeDescription },
                    { header: "Bi-Directional", dataKey: "biDirectional", width: 100, renderFunc: (config) => (config?.biDirectional ? "Yes" : "No") },
                    { header: "Distance (m)", renderFunc: getDistanceDescription, width: 150 },
                    { header: "Angle (°)", renderFunc: getAngleDescription, width: 100 },
                    { header: "Speed (m/s)", renderFunc: getSpeedDescription, width: 120 },
                    { header: "Target Type", dataKey: "targetType", width: 120, renderFunc: getTargetTypeDescription },
                    {
                        header: "Radar Tracker",
                        renderFunc: (config) => {
                            if (!config?.radarTrackerConfigId) return "None";
                            const trackerConfig = trackerConfigs?.find((tc) => tc.id === config.radarTrackerConfigId);
                            return trackerConfig?.name || "Unknown";
                        },
                        width: 140,
                    },
                    {
                        header: "Video Tracker",
                        renderFunc: (config) => {
                            if (!config?.videoTrackerConfigId) return "None";
                            const trackerConfig = trackerConfigs?.find((tc) => tc.id === config.videoTrackerConfigId);
                            return trackerConfig?.name || "Unknown";
                        },
                        width: 140,
                    },
                    {
                        header: (
                            <>
                                <IconLink Icon={Icons.PlusCircle} onClick={handleAddNewOnClick}>
                                    Add New
                                </IconLink>
                            </>
                        ),
                        renderFunc: (fg) => (
                            <IconLink
                                Icon={Icons.Trash}
                                onClick={(e) => {
                                    handleDeleteOnClick(fg!);
                                    e.stopPropagation();
                                }}
                            >
                                Delete
                            </IconLink>
                        ),
                        width: 75,
                    },
                ]}
            />
            <div className="flex place-items-center justify-center p-5">
                <Button onClick={handleAddNewOnClick}>Add New Chirp Config</Button>
            </div>
        </>
    );

    return (
        <AuthorizeView claim={CustomClaimValue.SuperAdmin}>
            {isEditing ? editView : tableView}
            {confirmDialog.renderDialog()}
        </AuthorizeView>
    );
};
